import { Col, Row } from 'react-bootstrap';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useFetchAgencyChannelSettings from 'components/domain/channelSettings/useFetchAgencyChannelSettings';
import LeadChannelIcon from 'components/domain/lead/LeadChannelIcon';
import useFetchDetermineEnrollment from 'components/domain/vrbo/newListingDiscount/useFetchDetermineEnrollment';
import RadioGroupField from 'components/forms/radioGroupField/RadioGroupField';
import TextField from 'components/forms/textField/TextField';
import SelectField from 'components/forms/selectField/SelectField';
import useAppUser from 'hooks/useAppUser';
import useIsMobileScreen from 'hooks/useIsMobileScreen';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import { BookingType } from 'models/BookingType';
import { Channel } from 'models/Channels';
import { LeadChannel } from 'models/Leads';
import { PropertyBusinessType } from 'models/Properties';
import {
  FormSectionColumn,
  SectionTitleContainer,
} from 'pages/property/common/Common.styles';
import PropertyFormFieldLabel from 'pages/property/common/PropertyFormFieldLabel';
import TooltipContentWithLearnMore from 'pages/property/common/tooltip/TooltipContentWithLearnMore';
import { Breakpoint, breakpoints } from 'styles/mediaQuery';
import { PropertySettingsMainTabFormValues } from '../../PropertySettingsMainTab.schema';
import usePropertySettingsInitData from '../../../usePropertySettingsInitData';
import { PropertyVrboSettingsCollapseStyled } from './PropertyVrboSettings.styles';
import PropertyVrboSettingsCancellationPolicyField from './PropertyVrboSettingsCancellationPolicyField';
import PropertyVrboSettingsInvoicePaymentMethodsLink from './PropertyVrboSettingsInvoicePaymentMethodsLink';
import PropertyVrboSettingsListedByField from './PropertyVrboSettingsListedByField';
import PropertyVrboSettingsPolicyPeriodsField from './PropertyVrboSettingsPolicyPeriodsField';
import { VRBO_AVAILABLE_BOOKING_TYPES } from './PropertyVrboSettings.constants';
import PropertyVrboSettingsPromotionsField from './PropertyVrboSettingsPromotionsField';

const PropertyVrboSettings = () => {
  const { agencyUid } = useAppUser();
  const isMobileScreen = useIsMobileScreen(breakpoints[Breakpoint.SM]);
  const { getCopyAddonIcon } = useCopyToClipboard();
  const { channelSettings, isFetching: isLoadingChannelSettings } =
    useFetchAgencyChannelSettings();
  const { property } = usePropertySettingsInitData();
  const { data: vrboDetermineEnrollment } = useFetchDetermineEnrollment({
    agencyUid,
    propertyUid: property?.uid,
  });
  const { t } = useTranslation();
  const {
    // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    vrboSettings: { propertyId: vrboPropertyId },
  } = useWatch<PropertySettingsMainTabFormValues>();

  const isVrboActive = !!channelSettings?.find(
    ({ enumId }) => enumId === Channel.homeaway,
  )?.isActive;

  const isHotel = property?.businessType === PropertyBusinessType.HOTEL;
  if (!isVrboActive || isLoadingChannelSettings || isHotel) {
    return null;
  }

  const sectionTitle = (
    <SectionTitleContainer>
      <LeadChannelIcon channel={LeadChannel.VRBO} />
      {t('pageProperty.mainSettings.vrboSettings.sectionTitle')}
    </SectionTitleContainer>
  );

  const bookingTypeFieldName = 'vrboSettings.bookingType';
  const bookingTypeOptions = VRBO_AVAILABLE_BOOKING_TYPES.map(
    (type: BookingType) => ({
      value: type,
      label: t(`common.bookingType.${type}`),
    }),
  );

  const propertyLocationOptions = [true, false].map((value) => ({
    label: t(
      `pageProperty.mainSettings.vrboSettings.showPropertyLocation.option_${value}`,
    ),
    value: `${value}`,
  }));

  const newListingDiscountOptions = [
    { label: t('common.disabled'), value: 'false' },
    { label: t('common.enabled'), value: 'true' },
  ];

  const isNewListingDiscountDisabled =
    !vrboDetermineEnrollment ||
    !!vrboDetermineEnrollment.ineligibilityReasons.length;

  return (
    <PropertyVrboSettingsCollapseStyled
      data-testid="vrbo-settings"
      defaultExpanded
      label={sectionTitle}
      variant="card"
    >
      <fieldset>
        <Row>
          <FormSectionColumn>
            <TextField
              label={t('pageProperty.mainSettings.vrboSettings.propertyId')}
              name="vrboSettings.propertyId"
              colSmInput={7}
              colSmLabel={5}
              disabled
              rightAddons={[getCopyAddonIcon(vrboPropertyId)]}
            />
            <SelectField
              label={
                <PropertyFormFieldLabel name={bookingTypeFieldName}>
                  {t('pageProperty.mainSettings.vrboSettings.bookingType')}
                </PropertyFormFieldLabel>
              }
              name={bookingTypeFieldName}
              colSmInput={7}
              colSmLabel={5}
              options={bookingTypeOptions}
              required
            />
            <PropertyVrboSettingsCancellationPolicyField />
            {isMobileScreen && <PropertyVrboSettingsPolicyPeriodsField />}
          </FormSectionColumn>
          <FormSectionColumn className="vrbo-settings-column-2">
            <RadioGroupField
              label={t(
                'pageProperty.mainSettings.vrboSettings.showPropertyLocation.label',
              )}
              name="vrboSettings.showPropertyExactLocation"
              options={propertyLocationOptions}
              colSmInput={7}
              colSmLabel={5}
            />
            <RadioGroupField
              disabled={isNewListingDiscountDisabled}
              horizontal
              label={t(
                'pageProperty.mainSettings.vrboSettings.newListingDiscount',
              )}
              name="vrboSettings.newListingDiscount"
              options={newListingDiscountOptions}
              colSmInput={7}
              colSmLabel={5}
              rightTooltip={
                <TooltipContentWithLearnMore
                  labelTooltipText={t(
                    'pageProperty.mainSettings.vrboSettings.newListingDiscountTooltip',
                  )}
                  learnMoreUrl="https://help.hostfully.com/en/articles/8694980-activate-vrbo-new-listing-discount"
                />
              }
            />
            <PropertyVrboSettingsListedByField />

            <PropertyVrboSettingsPromotionsField />
          </FormSectionColumn>
        </Row>
        {!isMobileScreen && (
          <Row>
            <Col className="policy-periods-column" xs={12}>
              <PropertyVrboSettingsPolicyPeriodsField />
            </Col>
          </Row>
        )}
        <Row>
          <Col xs={12}>
            <PropertyVrboSettingsInvoicePaymentMethodsLink />
          </Col>
        </Row>
      </fieldset>
    </PropertyVrboSettingsCollapseStyled>
  );
};

export default PropertyVrboSettings;
